import React, { useEffect, useRef } from 'react';
import type { NewsItem as NewsItemType } from '../types/news';
import { NewsItem } from './NewsItem';

interface NewsListProps {
  news: NewsItemType[];
  loading: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  error?: Error;
  onNewsClick: (news: NewsItemType) => void;
  onRetry: () => void;
  onLoadMore?: () => void;
}

export const NewsList: React.FC<NewsListProps> = ({
  news,
  loading,
  loadingMore = false,
  hasMore = false,
  error,
  onNewsClick,
  onRetry,
  onLoadMore,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingMore) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loadingMore]);
  if (error && news.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] p-12 bg-white border-2 border-red-200 rounded-2xl shadow-2xl">
        <div className="text-red-800 text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h2 className="text-3xl font-bold mb-3">Error Loading News</h2>
          <p className="text-gray-700 text-lg mb-6">
            {error?.message || 'Unknown error'}
          </p>
        </div>
        <button
          onClick={onRetry}
          className="px-8 py-4 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-all duration-200 font-bold text-lg shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-offset-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (news.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] p-12 bg-white border-2 border-gray-200 rounded-2xl shadow-2xl">
        <div className="text-gray-800 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📰</span>
          </div>
          <h2 className="text-3xl font-bold mb-3">No News Available</h2>
          <p className="text-gray-600 text-lg mb-6">
            Unable to load news at the moment. Please try again later.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <p className="text-sm text-gray-500">
              Check your internet connection and try refreshing the page
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="news-list">
      {/* ── Mobile: flat divider list, no cards ── */}
      <div className="sm:hidden">
        <div className="divide-y divide-gray-100">
          {news.map(item => (
            <NewsItem key={item.id} news={item} onClick={onNewsClick} featured={false} mobile />
          ))}
        </div>
      </div>

      {/* ── Desktop: featured + grid ── */}
      <div className="hidden sm:block">
        {news.length > 0 && (
          <div className="mb-8">
            <NewsItem news={news[0]} onClick={onNewsClick} featured={true} />
          </div>
        )}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {news.slice(1).map(item => (
            <NewsItem key={item.id} news={item} onClick={onNewsClick} featured={false} />
          ))}
        </div>
      </div>

      {/* Sentinel for IntersectionObserver — triggers next page */}
      {hasMore && !loadingMore && <div ref={sentinelRef} className="h-1" />}

      {/* Loading more indicator */}
      {(loading || loadingMore) && (
        <div className="flex justify-center py-8">
          <div className="flex items-center gap-3 text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-400 border-t-transparent"></div>
            <span className="text-sm font-medium">Loading more news…</span>
          </div>
        </div>
      )}

      {/* End-of-list message */}
      {!hasMore && !loading && news.length > 0 && (
        <p className="text-center text-xs text-gray-400 py-6">You've reached the end</p>
      )}

      {/* Error for subsequent loads */}
      {error && news.length > 0 && (
        <div className="flex items-center justify-between mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error.message || 'Failed to load more'}</p>
          <button
            onClick={onRetry}
            className="ml-4 px-3 py-1.5 bg-red-700 text-white rounded text-xs font-semibold hover:bg-red-800"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};
